/**
 * 产品信息页「站点」列的取值与展示项组装。
 *
 * 列表接口和价格接口都会给站点，但语义不同：
 * - `row.listedSites`：货品实际绑定的售卖站点（半托 `productSemiManaged.bindSites`，
 *   已由后端 `TemuGoodsProductMapper.bindSiteNames` 抽成名称数组）；
 * - `row.sitePrices`：价格接口的站点供货价明细（半托 `siteSupplierPrices`）。
 *
 * 两者集合不保证一致，展示时取并集，避免某个站点「有价格但列表里看不到」。
 */

/** SKC 关联站点（searchProduct.skcSiteShelfStatusList / spu-info.shelfStatusInfoList） */
export function listedSitesOf(row) {
  const raw = row?.listedSites;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const site = String(item || '').trim();
    if (!site || seen.has(site)) continue;
    seen.add(site);
    out.push(site);
  }
  return out;
}

export function resolveListedSiteNames(row, siteNameByAbbr, resolveName) {
  return listedSitesOf(row)
    .map((site) => resolveName(site, siteNameByAbbr))
    .filter(Boolean);
}

function priceBySiteName(row) {
  const map = new Map();
  for (const item of Array.isArray(row?.sitePrices) ? row.sitePrices : []) {
    const name = String(item?.siteName || '').trim();
    if (!name || map.has(name)) continue;
    map.set(name, item);
  }
  return map;
}

/**
 * 组装「站点」列的展示项：`[{ name, price, currency }]`。
 *
 * @param row             SKC 的代表 SKU 行
 * @param siteNameByAbbr  站点缩写 → 名称映射
 * @param resolveName     站点名解析函数（各平台 siteList 工具提供）
 */
export function buildSiteEntries(row, siteNameByAbbr, resolveName) {
  const prices = priceBySiteName(row);
  const out = [];
  const seen = new Set();
  const push = (name, price) => {
    if (!name || seen.has(name)) return;
    seen.add(name);
    out.push({
      name,
      price: price?.supplierPrice ?? null,
      currency: price?.currency || row?.currency || null,
    });
  };
  for (const name of resolveListedSiteNames(row, siteNameByAbbr, resolveName)) {
    push(name, prices.get(name));
  }
  for (const [name, price] of prices) {
    push(name, price);
  }
  return out;
}
